/**
 * useHeartbeat — Heartbeat do Player.
 *
 * Responsabilidades:
 *  1. Heartbeat: atualiza `ultima_conexao` via RPC a cada 10 s.
 *  2. Desconexão urgente: fetch keepalive no evento pagehide garante o status
 *     offline mesmo no fechamento abrupto do Chromium embarcado.
 *  3. Detecção de offline pelo cron: pg_cron marca offline telas com
 *     ultima_conexao > 1 min — o heartbeat de 10 s mantém a tela ativa.
 *
 * NOTA: visibilitychange foi removido intencionalmente. Esse evento dispara
 * ao trocar de aba no browser de testes e causa falsos positivos de offline.
 * No TV Box (aba única), pagehide cobre o fechamento real do Chromium.
 *
 * NOTA: o RAM Watchdog (reload automático após 18h de uptime) foi removido
 * a pedido — causava reloads indesejados. Se RAM acumulada voltar a ser
 * problema, reavaliar com uma estratégia que não derrube o player em uso.
 */

import { useEffect, useRef } from 'react';
import { playerRepository } from '../../data/playerRepository';

const HEARTBEAT_INTERVAL_MS = 10_000;

const SUPABASE_URL      = import.meta.env.VITE_SUPABASE_URL      as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

interface UseHeartbeatOptions {
  telaId: string;
  /**
   * Desativa o heartbeat quando false (ex.: loading / error / revogado).
   * O intervalo e o cleanup só são registrados quando enabled = true.
   */
  enabled: boolean;
  /** Nome da tela e empresa — anexados aos eventos de Watchdog drenados no heartbeat. */
  telaNome?: string | null;
  empresaId?: string | null;
}

export function useHeartbeat({ telaId, enabled, telaNome, empresaId }: UseHeartbeatOptions): void {
  // cbRef: telaNome/empresaId chegam depois do primeiro fetch da tela — o
  // setInterval de longa duração precisa ler o valor atual, não o capturado no mount.
  const contextoRef = useRef({ telaNome, empresaId });
  useEffect(() => { contextoRef.current = { telaNome, empresaId }; });

  useEffect(() => {
    if (!enabled) return;

    // Heartbeat imediato ao habilitar (não aguarda o primeiro intervalo)
    playerRepository.atualizarHeartbeat(telaId, contextoRef.current);

    const interval = setInterval(() => {
      playerRepository.atualizarHeartbeat(telaId, contextoRef.current);
    }, HEARTBEAT_INTERVAL_MS);

    // ── Desconexão urgente via sendBeacon ────────────────────────────────────
    // sendBeacon garante entrega mesmo após a aba ser destruída, sem bloquear
    // o teardown do processo do browser (pagehide / visibilitychange hidden).
    const marcarOfflineUrgente = () => {
      // fetch keepalive: entrega garantida mesmo após a página fechar, igual ao
      // sendBeacon, mas suporta headers customizados (apikey). Sem credentials,
      // compatível com Access-Control-Allow-Origin: * do Supabase.
      fetch(`${SUPABASE_URL}/rest/v1/rpc/marcar_tela_offline`, {
        method:    'POST',
        keepalive: true,
        headers:   { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY },
        body:      JSON.stringify({ p_tela_id: telaId }),
      }).catch(() => { /* silencia erros de rede no teardown */ });
    };

    window.addEventListener('pagehide', marcarOfflineUrgente);

    return () => {
      clearInterval(interval);
      window.removeEventListener('pagehide', marcarOfflineUrgente);
      // Não chamar marcarOffline no cleanup: dispara ao navegar para outra rota no
      // browser do admin (sessão autenticada → UPDATE funciona → TV real vai offline).
      // pagehide + keepalive acima cobre o fechamento real do Chromium da TV Box.
    };
  }, [telaId, enabled]);
}
